import { type ElementType } from 'react';
import { Card, type CardShadow } from '../Card';
import { Typography } from '../Typography';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * ProductCard component props.
 *
 * Sealed — `className` is not exposed. ProductCard is a fixed-layout design
 * system component. If a new visual variant is needed, add a prop here rather
 * than reaching for a className override.
 */
export interface ProductCardProps {
  /** URL of the product image. Displayed in the left pane, cropped to a square. */
  imageUrl: string;
  /** Alt text for the product image. Defaults to an empty string. */
  imageAlt?: string;
  /** Brand / manufacturer name. Rendered in body/small bodylight. */
  brandName: string;
  /** Product display name. Rendered in header/h3. */
  productName: string;
  /**
   * Shadow depth forwarded to the inner Card.
   * @see CardShadow
   */
  shadow?: CardShadow;
  /**
   * Root HTML element for the inner Card.
   * @default "div"
   */
  as?: ElementType;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer ProductCard component.
 *
 * A fixed-layout card that presents a product image alongside its brand name
 * and product name. The image occupies 40% of the card width as a square crop;
 * a vertical divider separates it from the text content pane.
 *
 * ### Layout
 * - **Left pane** — `w-2/5` square image, `object-cover object-center`
 * - **Vertical divider** — `border-l border-default`
 * - **Right pane** — `p-4 gap-3`, brand (body/small/bodylight) + product name (header/h3)
 *
 * ### Sealed component
 * `className` is not exposed. Layout (width, margin, grid placement) belongs
 * on the parent container. If a new visual state is needed, add a prop.
 *
 * @example
 * <ProductCard
 *   imageUrl="/images/nespresso.png"
 *   imageAlt="Nespresso Vertuo Pop Yellow"
 *   brandName="Nespresso"
 *   productName="Nespresso Vertuo Pop Yellow"
 * />
 *
 * @example
 * // With shadow + semantic root element
 * <ProductCard
 *   imageUrl="/images/nespresso.png"
 *   brandName="Nespresso"
 *   productName="Nespresso Vertuo Pop Yellow"
 *   shadow="first"
 *   as="article"
 * />
 */
export function ProductCard({
  imageUrl,
  imageAlt = '',
  brandName,
  productName,
  shadow,
  as,
}: ProductCardProps) {
  return (
    <Card shadow={shadow} as={as}>
      {/* ── Horizontal layout — p-4 all round, gap-3 between children ───────── */}
      <div className="flex flex-row items-center p-4 gap-3">
        {/* ── Left: product image — square, responsive width ───────────────── */}
        <div className="w-2/5 aspect-square flex-shrink-0">
          <img
            src={imageUrl}
            alt={imageAlt}
            className="w-full h-full object-cover object-center"
            onError={(e) => {
              e.currentTarget.src = '/assets/images/placeholder.svg';
            }}
          />
        </div>

        {/* ── Vertical divider — stretches to full row height ──────────────── */}
        <div className="border-l border-default self-stretch" aria-hidden="true" />

        {/* ── Right: text content ──────────────────────────────────────────── */}
        <div className="flex flex-col gap-1 justify-center flex-1 min-w-0">
          <Typography variant="body" size="small" color="bodylight" className="truncate">
            {brandName}
          </Typography>
          <Typography variant="header" size="h3" as="h3" className="line-clamp-2">
            {productName}
          </Typography>
        </div>
      </div>
    </Card>
  );
}

export default ProductCard;
